import React, { useState, useEffect } from 'react';
import {
  getFirestore,
  collection,
  addDoc,
  query,
  where,
  getDocs,
  doc,
  deleteDoc,
  updateDoc,
} from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import InputMask from 'react-input-mask';
import { auth } from '../../firebase';
import Layout from '../../components/layout/Layout';
import ClinicEditModal from '../../components/modals/ClinicEditModal';
import MessageModal from '../../components/modals/MessageModal';
import AutoFillModal from '../../components/modals/AutoFillModal';
import { fetchCNPJData } from '../../services/cnpjService';
import Spinner from '../../components/common/Spinner';
import Button from '../../components/common/Button';
import { FaThList, FaThLarge } from 'react-icons/fa';

function ClinicasPage() {
  const [clinicas, setClinicas] = useState([]);
  const [novaClinica, setNovaClinica] = useState({
    nome: '',
    endereco: '',
    telefone: '',
    cnpj: '',
    email: '',
    representante: '',
  });
  const [clinicaEditada, setClinicaEditada] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isAutoFillModalOpen, setIsAutoFillModalOpen] = useState(false);
  const [clinicaToRemove, setClinicaToRemove] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [autoFillData, setAutoFillData] = useState(null);
  const [user] = useAuthState(auth);
  const db = getFirestore();

  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    if (user && user.uid) {
      const fetchClinicas = async () => {
        try {
          const q = query(
            collection(db, 'clinicas'),
            where('userId', '==', user.uid)
          );
          const querySnapshot = await getDocs(q);
          const clinicasData = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setClinicas(clinicasData);
        } catch (error) {
          setError('Erro ao buscar clínicas. Tente novamente mais tarde.');
          console.error('Erro ao buscar clínicas:', error);
        }
      };

      fetchClinicas();
    }
  }, [user, db]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNovaClinica({ ...novaClinica, [name]: value });
  };

  const handleAutoFillConfirm = () => {
    if (autoFillData) {
      setNovaClinica((prevState) => ({
        ...prevState,
        ...autoFillData,
      }));
    }
    setIsAutoFillModalOpen(false);
  };

  const handleAutoFillCancel = () => {
    setIsAutoFillModalOpen(false);
  };

  const buscarDadosCNPJ = async () => {
    setIsLoading(true);
    try {
      const cnpjOnlyNumbers = novaClinica.cnpj.replace(/\D/g, '');

      const data = await fetchCNPJData(cnpjOnlyNumbers);

      if (data) {
        setAutoFillData({
          nome: `${data.nome} (${data.fantasia || ''})`,
          endereco:
            `${data.logradouro}, ${data.numero} ${data.complemento || ''}`.trim(),
          telefone: data.telefone || '',
          email: data.email || '',
          representante: data.qsa[0]?.nome || '',
        });
        setIsAutoFillModalOpen(true);
      } else {
        setError('CNPJ inválido ou não encontrado.');
      }
    } catch (error) {
      setError('Erro ao buscar CNPJ. Tente novamente.');
      console.error('Erro ao buscar CNPJ:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const adicionarClinica = async () => {
    if (
      novaClinica.nome &&
      novaClinica.endereco &&
      novaClinica.telefone &&
      novaClinica.cnpj &&
      novaClinica.email &&
      novaClinica.representante
    ) {
      try {
        const docRef = await addDoc(collection(db, 'clinicas'), {
          ...novaClinica,
          userId: user.uid,
        });
        setClinicas([...clinicas, { id: docRef.id, ...novaClinica }]);
        setNovaClinica({
          nome: '',
          endereco: '',
          telefone: '',
          cnpj: '',
          email: '',
        });
        setError('');
      } catch (e) {
        setError('Erro ao adicionar clínica. Tente novamente.');
        console.error('Erro ao adicionar clínica:', e);
      }
    } else {
      setError('Por favor, preencha todos os campos.');
    }
  };

  const handleRemoveClick = (clinica) => {
    setClinicaToRemove(clinica);
    setIsConfirmModalOpen(true);
  };

  const handleConfirmRemove = async () => {
    try {
      await deleteDoc(doc(db, 'clinicas', clinicaToRemove.id));
      setClinicas(
        clinicas.filter((clinica) => clinica.id !== clinicaToRemove.id)
      );
      setIsConfirmModalOpen(false);
    } catch (e) {
      setError('Erro ao remover clínica. Tente novamente.');
      console.error('Erro ao remover clínica:', e);
    }
  };

  const handleCancelRemove = () => {
    setIsConfirmModalOpen(false);
    setClinicaToRemove(null);
  };

  const abrirModalEdicao = (clinica) => {
    setClinicaEditada(clinica);
    setIsModalOpen(true);
  };

  const fecharModal = () => {
    setIsModalOpen(false);
    setClinicaEditada(null);
  };

  const editarClinica = async (updatedClinica) => {
    try {
      const clinicaRef = doc(db, 'clinicas', updatedClinica.id);
      await updateDoc(clinicaRef, updatedClinica);
      setClinicas(
        clinicas.map((clinica) =>
          clinica.id === updatedClinica.id ? updatedClinica : clinica
        )
      );
      fecharModal();
    } catch (e) {
      setError('Erro ao editar clínica. Tente novamente.');
      console.error('Erro ao editar clínica:', e);
    }
  };

  return (
    <Layout>
      <section className="clinicas-page">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-3xl font-semibold">Cadastro de Clínicas</h2>
          <div className="flex space-x-4">
            <button
              className={`p-2 rounded-full ${
                viewMode === 'grid' ? 'bg-primary text-white' : 'bg-gray-200'
              }`}
              onClick={() => setViewMode('grid')}
            >
              <FaThLarge />
            </button>
            <button
              className={`p-2 rounded-full ${
                viewMode === 'list' ? 'bg-primary text-white' : 'bg-gray-200'
              }`}
              onClick={() => setViewMode('list')}
            >
              <FaThList />
            </button>
          </div>
        </div>
        {error && (
          <div
            className="error-message text-red-500 font-semibold"
            aria-live="polite"
          >
            {error}
          </div>
        )}
        {isLoading && <Spinner />}
        {clinicas.length > 0 ? (
          <ul
            className={`clinicas-list grid gap-4 ${
              viewMode === 'grid'
                ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                : 'list'
            }`}
          >
            {clinicas.map((clinica) => (
              <li
                key={clinica.id}
                className="clinica-item bg-white shadow-md p-4 rounded-lg transition-transform duration-300 hover:shadow-lg"
              >
                <div className="clinica-info">
                  <h4 className="font-semibold text-lg text-teal-700">
                    {clinica.nome}
                  </h4>
                  <p className="text-sm">
                    <strong>Endereço:</strong> {clinica.endereco}
                  </p>
                  <p className="text-sm">
                    <strong>Telefone:</strong> {clinica.telefone}
                  </p>
                  <p className="text-sm">
                    <strong>CNPJ:</strong> {clinica.cnpj}
                  </p>
                  <p className="text-sm">
                    <strong>Email:</strong> {clinica.email}
                  </p>
                  <p className="text-sm">
                    <strong>Representante:</strong> {clinica.representante}
                  </p>
                  <div className="button-group flex space-x-2 mt-4">
                    <Button
                      type="remover"
                      label="Remover"
                      onClick={() => handleRemoveClick(clinica)}
                      className="bg-red-500 text-white hover:bg-red-600 px-4 py-2 rounded-lg"
                      ariaLabel={`Remover clínica ${clinica.nome}`}
                    />
                    <Button
                      type="editar"
                      label="Editar"
                      onClick={() => abrirModalEdicao(clinica)}
                      className="bg-blue-500 text-white hover:bg-blue-600 px-4 py-2 rounded-lg"
                      ariaLabel={`Editar clínica ${clinica.nome}`}
                    />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p>Nenhuma clínica cadastrada.</p>
        )}

        <section className="adicionar-clinica bg-white shadow-md rounded-lg p-6 mt-6">
          <h3 className="text-teal-600 text-xl font-semibold mb-4">
            Adicionar Clínica
          </h3>
          <div className="input-row grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="input-group">
              <label
                htmlFor="cnpj"
                className="block text-sm font-medium text-gray-700"
              >
                CNPJ:
              </label>
              <InputMask
                mask="99.999.999/9999-99"
                value={novaClinica.cnpj}
                onChange={handleInputChange}
              >
                {(inputProps) => (
                  <input
                    {...inputProps}
                    type="text"
                    id="cnpj"
                    name="cnpj"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                    placeholder="CNPJ da Clínica"
                    aria-required="true"
                  />
                )}
              </InputMask>
              <Button
                type="pesquisar"
                label="Auto Preencher"
                onClick={buscarDadosCNPJ}
                className="mt-3 bg-teal-500 text-white hover:bg-teal-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500 w-full"
              />
            </div>

            <div className="input-group">
              <label
                htmlFor="nome"
                className="block text-sm font-medium text-gray-700"
              >
                Nome:
              </label>
              <input
                type="text"
                id="nome"
                name="nome"
                placeholder="Nome da Clínica"
                value={novaClinica.nome}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                aria-required="true"
              />
            </div>
          </div>

          <div className="input-row grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
            <div className="input-group">
              <label
                htmlFor="endereco"
                className="block text-sm font-medium text-gray-700"
              >
                Endereço:
              </label>
              <input
                type="text"
                id="endereco"
                name="endereco"
                placeholder="Endereço da Clínica"
                value={novaClinica.endereco}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                aria-required="true"
              />
            </div>
            <div className="input-group">
              <label
                htmlFor="telefone"
                className="block text-sm font-medium text-gray-700"
              >
                Telefone:
              </label>
              <InputMask
                mask={
                  novaClinica.telefone.length > 11
                    ? '(99) 9999-9999'
                    : '(99) 9.9999-9999'
                }
                value={novaClinica.telefone}
                onChange={handleInputChange}
              >
                {(inputProps) => (
                  <input
                    {...inputProps}
                    type="text"
                    id="telefone"
                    name="telefone"
                    placeholder="Telefone da Clínica"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                    aria-required="true"
                  />
                )}
              </InputMask>
            </div>
          </div>

          <div className="input-row grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
            <div className="input-group">
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email:
              </label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Email da Clínica"
                value={novaClinica.email}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                aria-required="true"
              />
            </div>

            <div className="input-group">
              <label
                htmlFor="representante"
                className="block text-sm font-medium text-gray-700"
              >
                Representante Legal:
              </label>
              <input
                type="text"
                id="representante"
                name="representante"
                placeholder="Nome do Representante Legal"
                value={novaClinica.representante}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                aria-required="false"
              />
            </div>
          </div>

          <Button
            type="salvar"
            label="Adicionar"
            onClick={adicionarClinica}
            className="bg-teal-500 text-white hover:bg-teal-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500 mt-6 py-2"
          />
        </section>

        {isModalOpen && (
          <ClinicEditModal
            isOpen={isModalOpen}
            onClose={fecharModal}
            clinica={clinicaEditada}
            onSave={editarClinica}
          />
        )}

        <MessageModal
          isOpen={isConfirmModalOpen}
          onClose={handleCancelRemove}
          onConfirm={handleConfirmRemove}
          title="Confirmação de Remoção"
          message={`Tem certeza que deseja remover a clínica: ${clinicaToRemove?.nome}?`}
        />

        {isAutoFillModalOpen && (
          <AutoFillModal
            isOpen={isAutoFillModalOpen}
            onConfirm={handleAutoFillConfirm}
            onClose={handleAutoFillCancel}
            title="Confirmação de Auto Preenchimento"
            message="Deseja preencher os campos automaticamente com as informações do CNPJ encontradas na receita federal?"
            isError={false}
          />
        )}
      </section>
    </Layout>
  );
}

export default ClinicasPage;
